import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookings } from '../../hooks/useBookings';
import type { Booking, BookingStatus, ServiceType } from '../../mocks/bookings';
import BookingTable from './components/BookingTable';
import BookingDetailModal from './components/BookingDetailModal';
import { agencyInfo } from '../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const serviceTypes: ('All' | ServiceType)[] = ['All', 'Package', 'Flight', 'Hotel', 'Tour', 'Visa', 'Transfer', 'Cruise'];
const bookingStatuses: ('All' | BookingStatus)[] = ['All', 'Confirmed', 'Pending', 'On Hold', 'Cancelled', 'Completed'];

export default function BookingsPage() {
  const navigate = useNavigate();
  const { bookings, loading, updateBookingStatus } = useBookings();
  const [selected, setSelected] = useState<Booking | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | BookingStatus>('All');
  const [typeFilter, setTypeFilter] = useState<'All' | ServiceType>('All');

  const stats = useMemo(() => {
    const total = bookings.length;
    const confirmed = bookings.filter((b) => b.status === 'Confirmed').length;
    const revenue = bookings.filter((b) => b.status !== 'Cancelled').reduce((s, b) => s + b.totalAmount, 0);
    const pending = bookings.filter((b) => b.payStatus === 'Partial' || b.payStatus === 'Unpaid').reduce((s, b) => s + (b.totalAmount - b.paidAmount), 0);
    const upcoming = bookings.filter((b) => b.travelDate >= '2026-03-27' && b.status === 'Confirmed').length;
    return { total, confirmed, revenue, pending, upcoming };
  }, [bookings]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      const q = search.toLowerCase();
      const matchSearch =
        !search ||
        b.id.toLowerCase().includes(q) ||
        b.customer.toLowerCase().includes(q) ||
        b.destination.toLowerCase().includes(q) ||
        b.service.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'All' || b.status === statusFilter;
      const matchType = typeFilter === 'All' || b.serviceType === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
  }, [bookings, search, statusFilter, typeFilter]);

  const handleStatusChange = (id: string, status: BookingStatus) => {
    updateBookingStatus(id, status);
    if (selected?.id === id) {
      setSelected((prev) => prev ? { ...prev, status } : prev);
    }
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Bookings</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage all travel bookings, suppliers and payments</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/bookings/new')}
              className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ backgroundColor: brand }}
            >
              <i className="ri-add-line" />
              New Booking
            </button>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: 'Total Bookings', value: stats.total, icon: 'ri-suitcase-line', color: brand, sub: 'All time' },
            { label: 'Confirmed', value: stats.confirmed, icon: 'ri-checkbox-circle-line', color: '#059669', sub: 'Active' },
            { label: 'Upcoming', value: stats.upcoming, icon: 'ri-calendar-event-line', color: '#7C3AED', sub: 'Next 90 days' },
            { label: 'Total Revenue', value: `${(stats.revenue / 1000).toFixed(0)}K`, icon: 'ri-money-dollar-circle-line', color: '#D97706', sub: 'AED' },
            { label: 'Outstanding', value: `${(stats.pending / 1000).toFixed(0)}K`, icon: 'ri-time-line', color: '#DC2626', sub: 'AED pending' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${s.color}12` }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-800 leading-tight">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters & Table Card */}
      <div className="bg-white rounded-2xl border border-gray-100 flex flex-col">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative min-w-[220px] max-w-sm flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search by ID, customer, destination..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none bg-gray-50 placeholder-gray-400"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {bookingStatuses.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all whitespace-nowrap"
                style={
                  statusFilter === s
                    ? { backgroundColor: 'white', color: brand, fontWeight: 700 }
                    : { color: '#6B7280' }
                }
              >
                {s}
              </button>
            ))}
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as 'All' | ServiceType)}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white text-gray-600 focus:outline-none cursor-pointer"
          >
            {serviceTypes.map((t) => (
              <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
            ))}
          </select>

          <p className="text-xs text-gray-400 whitespace-nowrap ml-auto">{filtered.length} results</p>
        </div>

        {/* Table */}
        <div className="p-5">
          <BookingTable bookings={filtered} onSelect={setSelected} />
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
